import type React from 'react';

import * as styles from './styles';
import { Button, Popover, Typography } from '@cobliteam/ui/components';

import { Location } from '@cobliteam/common-types';

import type { TrackingDevice } from '@cobliteam/api-lib';
import { DeviceType } from '@cobliteam/api-lib/src/__generated__/graphql';

import { Analytics } from '@cobliteam/analytics';

import { CaptionDefault, Switch } from '@cobliteam/base-components';
import withPermission from '@cobliteam/base-components/src/PermissionsGate/PermissionsGate';

import Icon from '@cobliteam/ui/icons';

import MapMatch from './components/MapMatch/MapMatch';
import NoAccessHover from './components/NoAccessHover/NoAccessHover';

import TrackingPermissions from '../../../../../../permissions';

export interface EventToggleProps {
  roadSpeedEventActive: boolean;
  vehicleSpeedEventActive: boolean;
  geofencesActive: boolean;
  geofencesEventsActive: boolean;
  mediaEventsActive: boolean;
  mapMatchActive: boolean;
  fleetHasCamera: boolean;
  selectedDevice?: TrackingDevice;
  onToggleRoadSpeedEvents: (active: boolean) => void;
  onToggleVehicleSpeedEvents: (active: boolean) => void;
  onToggleGeofences: (active: boolean) => void;
  onToggleGeofencesEvents: (active: boolean) => void;
  onToggleMediaEvents: (active: boolean) => void;
  onToggleMapMatch: (active: boolean) => void;
}

export const EventToggle: React.FC<EventToggleProps> = ({
  roadSpeedEventActive,
  vehicleSpeedEventActive,
  geofencesActive,
  mediaEventsActive,
  geofencesEventsActive,
  mapMatchActive,
  fleetHasCamera,
  selectedDevice,
  onToggleRoadSpeedEvents,
  onToggleVehicleSpeedEvents,
  onToggleGeofences,
  onToggleMediaEvents,
  onToggleGeofencesEvents,
  onToggleMapMatch,
}) => {
  const handleToggleRoadSpeedEvents = () => onToggleRoadSpeedEvents(!roadSpeedEventActive);
  const handleToggleVehicleSpeedEvents = () => onToggleVehicleSpeedEvents(!vehicleSpeedEventActive);
  const handleToggleGeofences = () => onToggleGeofences(!geofencesActive);
  const handleToggleGeofencesEvents = () => onToggleGeofencesEvents(!geofencesEventsActive);
  const handleToggleMediaEvents = () => onToggleMediaEvents(!mediaEventsActive);
  const handleToggleMapMatch = () => onToggleMapMatch(!mapMatchActive);

  const handleCtaClick = () => {
    Analytics.track('Know More About Cobli Cam Clicked', {
      origin: 'tracking page toggle tooltip',
    });

    window.open(fleetHasCamera ? `${Location.VEHICLES}?upsell_modal=true` : Location.CAMERA_LP);
  };

  const RoadSpeedEventsToggle = withPermission(TrackingPermissions.VIEW_FLEET_SAFETY, () => (
    <div className={styles.toggle}>
      <Switch name='RoadSpeedEventsSwitch' onChange={handleToggleRoadSpeedEvents} checked={roadSpeedEventActive} />
      <CaptionDefault className={styles.text}>Velocidade da via excedida</CaptionDefault>
    </div>
  ));

  const VehicleSpeedEventToggle = withPermission(TrackingPermissions.VIEW_FLEET_SAFETY, () => (
    <div className={styles.toggle}>
      <Switch name='VehicleSpeedEventSwitch' onChange={handleToggleVehicleSpeedEvents} checked={vehicleSpeedEventActive} />
      <CaptionDefault className={styles.text}>Velocidade do veículo excedida</CaptionDefault>
    </div>
  ));

  const GeofencesToggle = withPermission(
    TrackingPermissions.VIEW_PLACES_OF_INTEREST,
    () => (
      <div className={styles.toggle}>
        <Switch name='GeofencesSwitch' onChange={handleToggleGeofences} checked={geofencesActive} />
        <CaptionDefault className={styles.text}>Locais de interesse</CaptionDefault>
      </div>
    ),
    () => <NoAccessHover />
  );

  const GeofencesEventsToggle = withPermission(
    TrackingPermissions.VIEW_PLACES_OF_INTEREST,
    () => (
      <div className={styles.toggle}>
        <Switch name='GeofencesEventsSwitch' onChange={handleToggleGeofencesEvents} checked={geofencesEventsActive} />
        <CaptionDefault className={styles.text}>Entrada e saída de locais de interesse</CaptionDefault>
      </div>
    ),
    () => <NoAccessHover />
  );

  const MediaEventsToggle = withPermission(TrackingPermissions.VIEW_CAMERA_MEDIA, () => {
    const showMediaRequestOption = !!(fleetHasCamera && selectedDevice && selectedDevice.type === DeviceType.Dashcam);
    const tooltipLabel = (
      <Popover>
        <Popover.Trigger>
          <Icon name='circle-help' color='gray' />
        </Popover.Trigger>
        <Popover.Content className='z-[1000] flex w-72 flex-col gap-2.5'>
          <Typography variant='description-title'>{showMediaRequestOption ? 'O que são eventos de vídeo?' : 'Eventos de vídeo'}</Typography>

          {showMediaRequestOption ? (
            <>
              <Typography variant='description-list-default'>
                São vídeos gerados automaticamente
                <br />
                pela câmera quando o motorista
                <br />
                comete eventos de risco durante a condução.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant='description-list-default'>
                Adquira a Cobli Cam para começar a
                <br />
                gravar os eventos de vídeo no trajeto.
              </Typography>
              <Button variant='link' size='condensed' onClick={handleCtaClick}>
                Saiba mais
              </Button>
            </>
          )}
        </Popover.Content>
      </Popover>
    );

    return (
      <div className={styles.toggle}>
        <Switch name='MediaEventsSwitch' onChange={handleToggleMediaEvents} checked={mediaEventsActive && showMediaRequestOption} disabled={!showMediaRequestOption} />
        <div className={styles.textWithTooltipWrapper}>
          <CaptionDefault className={styles.mediaText(showMediaRequestOption)}>Eventos de vídeo</CaptionDefault>
          {tooltipLabel}
        </div>
      </div>
    );
  });

  return (
    <div>
      <RoadSpeedEventsToggle />
      <VehicleSpeedEventToggle />
      <GeofencesToggle />
      <MediaEventsToggle />
      <GeofencesEventsToggle />
      <MapMatch handleToggleMapMatch={handleToggleMapMatch} mapMatchActive={mapMatchActive} />
    </div>
  );
};
